import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { TestModeService } from './test-mode';
import { ApiEnvelope } from './payment';

/** Shared filter for every AdminDashboard endpoint (all params optional). */
export interface AdminDashboardFilter {
  partnerId?: number;
  clientId?: number;
  source?: string; // 'PARTNER' | 'CLIENT'
  masterPolicyId?: number;
  gatewayId?: number; // payments only
  paymentStatus?: string; // Payments: SUCCESS/FAILED/… — Members: UNPAID/PAID/REFUNDED
  coiStatus?: string; // 'GENERATED' | 'NOT_GENERATED'
  kycStatus?: string; // NOT_VERIFIED / PENDING / VERIFIED
  status?: string; // member workflow status
  year?: number;
  month?: number; // 1-12, with year
  finYear?: number; // FY START year: 2025 => 01-Apr-2025..31-Mar-2026
  fromDate?: string; // 'yyyy-MM-dd' inclusive — overrides year/finYear
  toDate?: string; // 'yyyy-MM-dd' inclusive day
  search?: string;
  /** true = show test records, false = live. Sent on every AdminDashboard call. */
  isTestData?: boolean;
  page?: number;
  pageSize?: number; // default 20, max 200
}

// ---------- response row shapes (server serializes camelCase) ----------
export interface AdminPaymentRow {
  id: number;
  memberId: number;
  memberUniqueId?: string | null;
  memberName?: string | null;
  memberSeriesNo?: string | null;
  paymentUniqueId?: string | null;
  amount: number;
  currency?: string | null;
  paymentMode?: string | null;
  paymentStatus?: string | null;
  payerType?: string | null;
  gatewayName?: string | null;
  gatewayOrderId?: string | null;
  gatewayPaymentId?: string | null;
  failureReason?: string | null;
  paidAt?: string | null;
  createdDate?: string | null;
  source?: string | null;
  partnerId?: number | null;
  partnerName?: string | null;
  clientId?: number | null;
  clientName?: string | null;
  masterPolicyName?: string | null;
  policyNo?: string | null;
  coiGenerated?: boolean;
}

export interface AdminMemberRow {
  id: number;
  memberUniqueId?: string | null;
  memberSeriesNo?: string | null;
  fullName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  loanNumber?: string | null;
  masterPolicyName?: string | null;
  grossAmt?: number | null;
  paidAmount?: number | null;
  paymentStatus?: string | null;
  kycStatus?: string | null;
  status?: string | null;
  paidAt?: string | null;
  createdAt?: string | null;
  policyNo?: string | null;
  receiptNo?: string | null;
  policyStartDate?: string | null;
  policyEndDate?: string | null;
  coiGenerated?: boolean;
  source?: string | null;
  partnerId?: number | null;
  partnerName?: string | null;
  clientId?: number | null;
  clientName?: string | null;
}

/** Partner-wise / top-partners row (client-wise mirrors it with client fields). */
export interface AdminEntityRow {
  partnerId?: number;
  partnerCode?: string | null;
  partnerName?: string | null;
  clientId?: number;
  clientCode?: string | null;
  clientName?: string | null;
  companyName?: string | null;
  isActive?: boolean;
  totalMembers: number;
  paidMembers: number;
  unpaidMembers: number;
  totalPremium: number;
  collectedAmount: number;
  pendingAmount: number;
  policiesIssued: number;
}

export interface Paged<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface FilterOption {
  id: number;
  name: string;
  code?: string | null;
}

export interface AdminFilterOptions {
  partners: FilterOption[];
  clients: FilterOption[];
  gateways: FilterOption[];
  masterPolicies: FilterOption[];
  sources: string[];
  transactionStatuses: string[];
  memberPaymentStates: string[];
  kycStatuses: string[];
  coiStatuses: string[];
}

/**
 * Admin-only (backend [Authorize(Roles="Admin")] → non-admins get HTTP 403).
 * Every endpoint takes the same AdminDashboardFilter; responses are the standard
 * envelope with camelCase data — branch on `success`.
 */
@Injectable({ providedIn: 'root' })
export class AdminDashboardService {
  private http = inject(HttpClient);
  private view = inject(TestModeService);
  private readonly api = environment.apiUrl;

  overview(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope>(`${this.api}/AdminDashboard/Overview`, { params: this.params(f) });
  }
  payments(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope<Paged<AdminPaymentRow>>>(`${this.api}/AdminDashboard/Payments`, { params: this.params(f) });
  }
  members(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope<Paged<AdminMemberRow>>>(`${this.api}/AdminDashboard/Members`, { params: this.params(f) });
  }
  partnerWise(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope>(`${this.api}/AdminDashboard/PartnerWise`, { params: this.params(f) });
  }
  clientWise(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope>(`${this.api}/AdminDashboard/ClientWise`, { params: this.params(f) });
  }
  coiSummary(f: AdminDashboardFilter) {
    return this.http.get<ApiEnvelope>(`${this.api}/AdminDashboard/CoiSummary`, { params: this.params(f) });
  }
  filterOptions() {
    return this.http.get<ApiEnvelope<AdminFilterOptions>>(`${this.api}/AdminDashboard/FilterOptions`);
  }

  private params(f: AdminDashboardFilter): HttpParams {
    let p = new HttpParams();
    // The Live/Test view is a property of the SESSION, not of any one screen, so it is applied
    // centrally here — a page that forgot to pass it would silently show the wrong world.
    if (f.isTestData === undefined) f = { ...f, isTestData: this.view.viewTest() };
    Object.entries(f).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p = p.set(k, String(v));
    });
    return p;
  }
}
